import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Badge, Button, Card, EngineKeyline, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { generateCampaign, createContent, campaignPreviewToDraft } from '@/data/sources-social';
import { channelLabel } from '@/data/studio-layouts';
import type { AiProvenance, CampaignChannel, CampaignPreview, CampaignSpecResponse } from '@/types/social';

/* Prompt-to-Campaign AI Creator (v0).
 * One prompt + a channel preset -> POST /v1/social/studio/campaign -> validated,
 * approval-ready previews per channel. Advisory only: nothing publishes, nothing
 * is persisted until the human clicks "Add to drafts" (the existing content
 * create path). Honest DataState — connection_required / insufficient_signal are
 * rendered verbatim, no content is ever synthesised client-side. */

type ChannelPreset = { key: string; label: string; teach: string; channels: string[] };

const PRESETS: ChannelPreset[] = [
  { key: 'social', label: 'Social', teach: 'Instagram, Facebook, LinkedIn & X posts.', channels: ['instagram', 'facebook', 'linkedin', 'x'] },
  { key: 'blog', label: 'Blog', teach: 'A long-form article outline + copy.', channels: ['blog'] },
  { key: 'email', label: 'Email', teach: 'A campaign email with subject & body.', channels: ['email'] },
  { key: 'complete', label: 'Complete campaign', teach: 'Every channel — social, blog & email together.', channels: ['complete'] },
];

/** Friendly label incl. blog/email (studio-layouts only knows publish channels). */
function campaignChannelLabel(ch: CampaignChannel): string {
  if (ch === 'blog') return 'Blog';
  if (ch === 'email') return 'Email';
  return channelLabel(ch);
}

export function PromptCampaignPage() {
  const [prompt, setPrompt] = useState('');
  const [theme, setTheme] = useState('');
  const [objective, setObjective] = useState('');
  const [presetKey, setPresetKey] = useState('social');

  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[0];

  const gen = useMutation<CampaignSpecResponse, unknown, void>({
    mutationFn: () =>
      generateCampaign({
        prompt: prompt.trim(),
        channels: preset.channels,
        campaignTheme: theme.trim() || undefined,
        campaignObjective: objective.trim() || undefined,
      }),
  });

  const inputCls =
    'w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const result = gen.data;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Create from prompt"
        subtitle="Describe the campaign in one line, pick where it runs, and generate approval-ready copy, hooks, CTAs and a visual brief per channel. Everything is advisory — nothing publishes."
        actions={
          <div className="flex items-center gap-2">
            <a className="hidden text-xs text-text-secondary underline hover:text-text-primary sm:inline" href="/social/content-studio">Back to Studio</a>
            <Badge tone="ai">AI · Beta</Badge>
          </div>
        }
      />
      <EngineKeyline className="mb-6" />

      <Card className="mb-8 p-5 sm:p-6">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">Campaign prompt</span>
          <textarea
            className={inputCls}
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. Launch our new autumn yoga membership with a 2-week free trial for first-time members."
          />
        </label>

        <fieldset className="mt-4">
          <legend className="mb-2 block text-xs font-medium text-text-secondary">Where should it run?</legend>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => {
              const active = p.key === presetKey;
              return (
                <button
                  key={p.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setPresetKey(p.key)}
                  className={
                    'rounded-lg border px-4 py-2.5 text-left text-sm font-medium transition-colors ' +
                    (active
                      ? 'border-ai bg-ai/5 text-text-primary ring-1 ring-ai/30'
                      : 'border-border-default bg-surface text-text-primary hover:border-ai hover:bg-ai/5')
                  }
                >
                  <span className="block">{p.label}</span>
                  <span className="mt-0.5 block text-[11px] font-normal text-text-tertiary">{p.teach}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Theme <span className="text-text-tertiary">(optional)</span></span>
            <input className={inputCls} value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Autumn membership launch" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Goal <span className="text-text-tertiary">(optional)</span></span>
            <input className={inputCls} value={objective} onChange={(e) => setObjective(e.target.value)} placeholder="Drive free-trial sign-ups" />
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-text-tertiary">Visuals are a written brief + stock-media search suggestions — BookedCentral never generates images.</p>
          <Button
            variant="ai"
            onClick={() => gen.mutate()}
            disabled={gen.isPending || prompt.trim().length === 0}
          >
            {gen.isPending ? 'Generating…' : 'Generate campaign'}
          </Button>
        </div>
      </Card>

      {gen.isPending ? (
        <LoadingState rows={4} />
      ) : gen.isError ? (
        <ErrorState error={gen.error} onRetry={() => gen.mutate()} />
      ) : result ? (
        <CampaignResultView result={result} onRetry={() => gen.mutate()} />
      ) : null}
    </div>
  );
}

function CampaignResultView({ result, onRetry }: { result: CampaignSpecResponse; onRetry: () => void }) {
  if (!result.available) {
    return (
      <EmptyState
        title="AI Studio connection required"
        teach={result.message || 'The Campaign Creator requires the AI service to be enabled. No content was generated.'}
      />
    );
  }
  if (!result.valid) {
    const title = result.state === 'invalid_request' ? 'That request needs a prompt' : 'Not enough to work with';
    return (
      <EmptyState
        title={title}
        teach={result.message || 'The campaign could not be generated from this prompt. No content was produced.'}
        action={<Button variant="secondary" onClick={onRetry}>Try a different prompt</Button>}
      />
    );
  }

  const { campaign, previews, dropped, aiProvenance } = result;
  return (
    <section className="space-y-5">
      <div className="rounded-lg border border-border-subtle bg-surface p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-xl font-semibold text-text-primary">{campaign.name || 'Untitled campaign'}</h2>
          <Badge tone="ai">Advisory · human-approved</Badge>
        </div>
        {campaign.theme ? <p className="mt-1 text-sm text-text-secondary">{campaign.theme}</p> : null}
        {campaign.objective ? <p className="mt-1 text-xs text-text-tertiary">Goal: {campaign.objective}</p> : null}
        {/* internal AI provenance (model id / prompt version) is kept in the
            draft audit record on add-to-drafts, never rendered to the customer */}
      </div>

      {previews.length === 0 ? (
        <EmptyState title="No channels survived review" teach="The generated copy did not pass the honesty checks for any channel, so nothing is shown. Try a more specific prompt." />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {previews.map((p, i) => (
            <ChannelPreviewCard key={`${p.channel}-${i}`} preview={p} provenance={aiProvenance} />
          ))}
        </div>
      )}

      {dropped.length > 0 ? (
        <p className="text-xs text-text-tertiary">Dropped (unusable after review): {dropped.join(', ')}</p>
      ) : null}
    </section>
  );
}

function ChannelPreviewCard({ preview, provenance }: { preview: CampaignPreview; provenance: AiProvenance | null }) {
  const navigate = useNavigate();
  const add = useMutation({
    mutationFn: () => createContent(campaignPreviewToDraft(preview, provenance)),
  });
  const added = add.data;

  const chip = 'rounded-sm bg-inset px-1.5 py-0.5 text-[11px] text-text-secondary';

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">{campaignChannelLabel(preview.channel)}</span>
          <Badge tone="neutral">{preview.contentType}</Badge>
        </div>
        <div className="flex items-center gap-1.5">
          {preview.aspectRatio ? <span className={chip}>{preview.aspectRatio}</span> : null}
          {preview.layoutKey ? <span className={chip}>{preview.layoutKey}</span> : null}
        </div>
      </div>

      {preview.title ? <h3 className="font-display text-base font-semibold text-text-primary">{preview.title}</h3> : null}
      {preview.body ? <p className="whitespace-pre-wrap text-sm text-text-secondary">{preview.body}</p> : null}

      {preview.pages.length > 0 ? (
        <div className="space-y-2 rounded-md border border-border-subtle bg-canvas p-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Carousel · {preview.pages.length} slides</span>
          {preview.pages.map((pg, idx) => (
            <div key={idx} className="text-xs">
              <span className="font-medium text-text-primary">{idx + 1}. {pg.heading}</span>
              {pg.body ? <p className="mt-0.5 text-text-secondary">{pg.body}</p> : null}
            </div>
          ))}
        </div>
      ) : null}

      {preview.hooks.length > 0 ? (
        <PreviewList label="Hooks" items={preview.hooks} />
      ) : null}
      {preview.ctas.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-medium text-text-tertiary">CTAs:</span>
          {preview.ctas.map((c, i) => <span key={i} className="rounded-full bg-ai/10 px-2 py-0.5 text-[11px] text-ai">{c}</span>)}
        </div>
      ) : null}
      {preview.hashtags.length > 0 ? (
        <p className="text-xs text-text-secondary">{preview.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}</p>
      ) : null}

      {preview.visualDirection ? (
        <div className="rounded-md border border-border-subtle bg-canvas p-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Visual direction</span>
          <p className="mt-1 text-xs text-text-secondary">{preview.visualDirection}</p>
          {preview.stockMediaQueries.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {preview.stockMediaQueries.map((q, i) => <span key={i} className={chip}>{q}</span>)}
            </div>
          ) : null}
          <p className="mt-2 text-[11px] text-text-tertiary">No AI image generation — stock-media suggestions only.</p>
        </div>
      ) : null}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        {added ? (
          <button className="text-xs font-medium text-ai underline" onClick={() => navigate(`/social/content-studio/${added.id}`)}>
            Added to drafts · open editor
          </button>
        ) : (
          <span className="text-[11px] text-text-tertiary">Review & edit after adding — approval is unchanged.</span>
        )}
        <Button
          variant={added ? 'secondary' : 'primary'}
          onClick={() => add.mutate()}
          disabled={add.isPending || Boolean(added)}
        >
          {add.isPending ? 'Adding…' : added ? 'Added' : 'Add to drafts'}
        </Button>
      </div>
      {add.isError ? <p role="alert" className="text-xs text-danger">Could not add this to drafts. Try again.</p> : null}
    </Card>
  );
}

function PreviewList({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <span className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">{label}</span>
      <ul className="mt-1 space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-text-secondary">• {it}</li>
        ))}
      </ul>
    </div>
  );
}
